import { ref, computed, onMounted } from 'vue';
import { resourcesService } from '@/api/resources.service';
import type { Praeparat } from "@/types/otc/praeparat";

interface Props {
    praeparat: Praeparat | null;
}

interface ImageConfig {
    width: number;
    quality: number;
    transparent: boolean;
}

/**
 * Verwaltet die Produktbild-Logik mit URL-Generierung und Fallbacks
 * Zuständig für Bildladen, URL-Aufbau und Entwicklungsumgebung-Anpassungen
 */
export function useOverviewImage(props: Props) {
    const baseUrl = ref<string>('');

    const IMAGE_CONFIG: ImageConfig = {
        width: 300,
        quality: 85,
        transparent: true
    };

    /**
     * Lädt die Basis-URL für die Bild-API
     */
    const loadImageApiUrl = async () => {
        try {
            baseUrl.value = await resourcesService.publicImageApiUrl();
            applyDevelopmentUrlFix();
        } catch (error) {
            console.error('Fehler beim Laden der Bild-API URL:', error);
        }
    };

    /**
     * Temporäre Anpassung für lokale Entwicklungsumgebung
     * TODO: Entfernen wenn nicht mehr benötigt
     */
    const applyDevelopmentUrlFix = () => {
        if (baseUrl.value.startsWith('http://localhost:57444')) {
            baseUrl.value = baseUrl.value.replace(
                'http://localhost:57444',
                'https://fachwelt.aliud.de'
            );
        }
    };

    /**
     * Extrahiert den Bildschlüssel aus den Produktdaten
     */
    const imageKey = computed(() => props.praeparat?.bildKey ?? '');

    /**
     * Erstellt die Basis-Bild-URL mit Schlüssel
     */
    const productImage = computed(() => {
        if (!baseUrl.value || !imageKey.value) return '';

        const url = new URL(baseUrl.value, window.location.origin);
        url.searchParams.set('key', imageKey.value);
        return url.toString();
    });

    /**
     * Erstellt die finale Bild-URL mit Größen- und Qualitätsparametern
     */
    const finalImageUrl = computed(() => {
        if (!productImage.value) return '';

        const params = new URLSearchParams({
            width: IMAGE_CONFIG.width.toString(),
            quality: IMAGE_CONFIG.quality.toString(),
            transparent: IMAGE_CONFIG.transparent.toString()
        });

        return `${productImage.value}&${params.toString()}`;
    });

    /**
     * Generiert den Alt-Text für das Bild
     */
    const altText = computed(() => {
        return props.praeparat?.name
            ? `Produktbild: ${props.praeparat.name}`
            : 'Produktbild';
    });

    /**
     * Prüft ob ein gültiges Bild vorhanden ist
     */
    const hasImage = computed(() => Boolean(finalImageUrl.value));

    onMounted(loadImageApiUrl);

    return {
        finalImageUrl,
        altText,
        hasImage
    };
}